// TennisTrack Hub — Pagina Registrazione | 029 Project
import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../utils/api';
import './Auth.css';

// SVG logo Google (inline, nessuna dipendenza)
const GoogleIcon = () => (
  <svg width="20" height="20" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
    <path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9.1 3.2l6.8-6.8C35.8 2.4 30.2 0 24 0 14.6 0 6.6 5.4 2.6 13.3l7.9 6.1C12.4 13 17.8 9.5 24 9.5z"/>
    <path fill="#4285F4" d="M46.1 24.5c0-1.6-.1-3.1-.4-4.5H24v8.5h12.4c-.5 2.8-2.1 5.1-4.4 6.7l7 5.4C43 36.7 46.1 31 46.1 24.5z"/>
    <path fill="#FBBC05" d="M10.5 28.6A14.5 14.5 0 0 1 9.5 24c0-1.6.3-3.2.8-4.6l-7.9-6.1A23.9 23.9 0 0 0 0 24c0 3.9.9 7.5 2.6 10.8l7.9-6.2z"/>
    <path fill="#34A853" d="M24 48c6.2 0 11.4-2 15.2-5.5l-7-5.4c-2.1 1.4-4.8 2.3-8.2 2.3-6.2 0-11.5-4.2-13.4-9.8l-7.9 6.1C6.6 42.6 14.6 48 24 48z"/>
  </svg>
);

const Register = () => {
  const [form, setForm] = useState({ nome: '', cognome: '', email: '', password: '', confermaPassword: '' });
  const [errore, setErrore] = useState('');
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const { register, loginDaToken } = useAuth();
  const navigate = useNavigate();
  const googleBtnRef = useRef(null);

  // Inizializza Google Identity Services (stessa logica di Login.js)
  useEffect(() => {
    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    if (!clientId || clientId === 'INSERISCI_IL_TUO_GOOGLE_CLIENT_ID.apps.googleusercontent.com') return;

    const scriptId = 'google-gsi-script';
    if (!document.getElementById(scriptId)) {
      const script = document.createElement('script');
      script.id = scriptId;
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      script.onload = () => initGoogle(clientId);
      document.head.appendChild(script);
    } else if (window.google) {
      initGoogle(clientId);
    }
  }, []);

  const initGoogle = (clientId) => {
    if (!window.google?.accounts?.id) return;
    window.google.accounts.id.initialize({
      client_id: clientId,
      callback: handleGoogleCallback,
      auto_select: false,
      cancel_on_tap_outside: true,
    });
  };

  const handleGoogleCallback = async (response) => {
    setGoogleLoading(true);
    setErrore('');
    try {
      // Stessa endpoint del login — se l'utente non esiste viene creato automaticamente
      const res = await api.post('/auth/google/verify', { credential: response.credential });
      loginDaToken(res.data.token, res.data.utente);
      navigate('/dashboard');
    } catch (err) {
      setErrore(err.response?.data?.error || 'Errore durante la registrazione con Google.');
    } finally {
      setGoogleLoading(false);
    }
  };

  const avviaGoogleLogin = () => {
    if (!window.google?.accounts?.id) {
      setErrore('Google Sign-In non disponibile. Ricarica la pagina o registrati con email e password.');
      return;
    }
    setErrore('');
    window.google.accounts.id.prompt((notification) => {
      if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
        window.google.accounts.id.renderButton(googleBtnRef.current, {
          theme: 'outline', size: 'large', width: 300
        });
      }
    });
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setErrore('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrore('');

    if (form.password !== form.confermaPassword) {
      return setErrore('Le password non coincidono.');
    }
    if (form.password.length < 8) {
      return setErrore('La password deve avere almeno 8 caratteri.');
    }

    setLoading(true);
    try {
      await register(form.nome, form.cognome, form.email, form.password);
      navigate('/dashboard');
    } catch (err) {
      setErrore(err.response?.data?.error || 'Errore durante la registrazione.');
    } finally {
      setLoading(false);
    }
  };

  const googleConfigured = process.env.REACT_APP_GOOGLE_CLIENT_ID &&
    process.env.REACT_APP_GOOGLE_CLIENT_ID !== 'INSERISCI_IL_TUO_GOOGLE_CLIENT_ID.apps.googleusercontent.com';

  return (
    <div className="auth-page">
      <div className="auth-container">

        {/* Logo */}
        <div className="auth-logo">
          <img src="/img/logo_tennistrack_300x800.png" alt="TennisTrack Hub" />
        </div>

        <div className="card auth-card">
          <h2 className="auth-title">Crea il tuo account</h2>
          <p className="auth-subtitle">Inizia a gestire i tuoi atleti</p>

          {errore && <div className="alert alert-error">{errore}</div>}

          {/* Bottone Google */}
          {googleConfigured && (
            <>
              <button
                type="button"
                className="btn-google"
                onClick={avviaGoogleLogin}
                disabled={googleLoading || loading}
              >
                <GoogleIcon />
                {googleLoading ? 'Registrazione in corso...' : 'Registrati con Google'}
              </button>

              <div className="auth-divider">oppure</div>
            </>
          )}

          <form onSubmit={handleSubmit}>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="nome">Nome</label>
                <input
                  type="text"
                  id="nome"
                  name="nome"
                  placeholder="Mario"
                  value={form.nome}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="cognome">Cognome</label>
                <input
                  type="text"
                  id="cognome"
                  name="cognome"
                  placeholder="Rossi"
                  value={form.cognome}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="email">Email</label>
              <input
                type="email"
                id="email"
                name="email"
                placeholder="tuaemail@esempio.com"
                value={form.email}
                onChange={handleChange}
                required
                autoComplete="email"
              />
            </div>

            <div className="form-group">
              <label htmlFor="password">Password</label>
              <input
                type="password"
                id="password"
                name="password"
                placeholder="Minimo 8 caratteri"
                value={form.password}
                onChange={handleChange}
                required
                autoComplete="new-password"
              />
            </div>

            <div className="form-group">
              <label htmlFor="confermaPassword">Conferma Password</label>
              <input
                type="password"
                id="confermaPassword"
                name="confermaPassword"
                placeholder="Ripeti la password"
                value={form.confermaPassword}
                onChange={handleChange}
                required
                autoComplete="new-password"
              />
            </div>

            <button
              type="submit"
              className="btn btn-primary btn-full"
              disabled={loading || googleLoading}
            >
              {loading ? 'Registrazione in corso...' : 'Crea Account'}
            </button>
          </form>

          <div className="auth-switch">
            Hai già un account? <Link to="/login">Accedi</Link>
          </div>

          {/* Div nascosto per fallback Google button render */}
          <div ref={googleBtnRef} style={{ display: 'none' }} />

          <p className="auth-privacy">
            Registrandoti accetti la nostra{' '}
            <a href="/privacy-policy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
            {' '}e la{' '}
            <a href="/cookie-policy" target="_blank" rel="noopener noreferrer">Cookie Policy</a>.
          </p>
        </div>

        {/* Footer 029 Project */}
        <div className="auth-footer">
          <img src="/img/madeby029.png" alt="029 Project" className="footer-logo" />
        </div>

      </div>
    </div>
  );
};

export default Register;
